import { supabase } from "./supabase";
import { chamar } from "./calculadoras";
import type { CstDaReceita, FichaDaReceita } from "./tabelaCst";
import type { MotivoAbertura } from "./abrir";

// O IO do CST e ClassTrib (000223) — zero regra.
//
// O catálogo NÃO mora no hub. Ele é da Receita, muda com a norma e é
// parametrizado por DATA; uma cópia aqui seria a segunda lista que envelhece em
// silêncio — a cicatriz das duas listas de CNAE, fechada pela 000189.
//
// O caminho é a Edge Function `calcular-tributos`, a mesma das Calculadoras:
// ela é um proxy com portão para a API do gov, e o `card` decide o ALCANCE —
// esta ferramenta só consulta tabela tributária, e não dispara cálculo. O
// navegador não alcança aquela API (medido: preflight 403, e a resposta simples
// sem `access-control-allow-origin`), então até a lista de CSTs passa por lá.
//
// O que é NOSSO são os favoritos, e eles guardam o CÓDIGO: quando a Receita
// reescrever o texto de um cClassTrib, o aluno continua com o mesmo item e
// passa a ver a redação nova.

export const CARD = "cst-e-classtrib";

/** As duas tabelas da Receita. O código `000001` existe nas duas. */
export type Tributo = "cbs-ibs" | "is";

export const TRIBUTOS: { id: Tributo; nome: string; resumo: string }[] = [
  {
    id: "cbs-ibs",
    nome: "IBS e CBS",
    resumo: "A tabela de situações e classificações tributárias do IBS e da CBS.",
  },
  {
    id: "is",
    nome: "Imposto Seletivo",
    resumo: "A tabela do Imposto Seletivo — o tributo sobre bens prejudiciais à saúde e ao meio ambiente.",
  },
];

export type Catalogo = { csts: CstDaReceita[]; fichas: FichaDaReceita[] };

export type Resposta<T> =
  | { situacao: "ok"; dados: T }
  | { situacao: "sem-permissao"; motivo: MotivoAbertura }
  | { situacao: "indisponivel"; motivo: string };

/**
 * Baixa as DUAS listas do gov, em paralelo.
 *
 * São duas idas porque são duas perguntas diferentes, e nenhuma responde
 * sozinha: a de situações diz **a qual CST cada classificação pertence** (e não
 * traz os detalhes), a de classificações traz a **ficha completa** (e não diz o
 * CST). Em paralelo, o custo é o da mais lenta, e não a soma.
 */
export async function lerCatalogo(tributo: Tributo, data: string): Promise<Resposta<Catalogo>> {
  const rotaCsts = tributo === "cbs-ibs" ? "csts" : "csts-is";
  const rotaFichas = tributo === "cbs-ibs" ? "classificacoes-cbs-ibs" : "classificacoes-is";

  const [csts, fichas] = await Promise.all([
    chamar<CstDaReceita[]>(rotaCsts, { card: CARD, params: { data } }),
    chamar<FichaDaReceita[]>(rotaFichas, { card: CARD, params: { data } }),
  ]);

  // A recusa de AUTORIZAÇÃO tem precedência sobre a indisponibilidade: se o
  // aluno não pode usar a ferramenta, dizer "o piloto está fora do ar" o faria
  // esperar por algo que nunca vai acontecer.
  for (const r of [csts, fichas]) {
    if (r.situacao === "sem-permissao") return { situacao: "sem-permissao", motivo: r.motivo };
  }
  if (csts.situacao !== "ok" || fichas.situacao !== "ok") {
    const ruim = csts.situacao !== "ok" ? csts : fichas;
    return { situacao: "indisponivel", motivo: "motivo" in ruim ? ruim.motivo : "falha" };
  }

  // Resposta com outra forma não derruba a tela: o piloto muda sem avisar, e
  // uma ferramenta que quebra em vez de mostrar menos perde tudo o que está
  // certo. Lista vazia vira "o catálogo veio vazio", que a tela sabe dizer.
  return {
    situacao: "ok",
    dados: {
      csts: Array.isArray(csts.dados) ? csts.dados : [],
      fichas: Array.isArray(fichas.dados) ? fichas.dados : [],
    },
  };
}

// ---------------------------------------------------------------------------
// Os favoritos
// ---------------------------------------------------------------------------
export type Favorito = {
  id: string;
  seq: number;
  tributo: Tributo;
  cst: string;
  cclasstrib: string | null;
  nota: string | null;
  criado_em: string;
};

export type Favoritos = { total: number; teto: number; favoritos: Favorito[] };

export async function lerFavoritos(): Promise<Favoritos | { motivo: MotivoAbertura } | null> {
  const { data, error } = await supabase.rpc("cst_meus_favoritos", { p_curso: cursoDaQuery() });
  if (error) return null;
  const r = (data ?? {}) as { ok?: boolean; motivo?: string } & Favoritos;
  if (!r.ok) return { motivo: (r.motivo ?? "falha") as MotivoAbertura };
  return {
    total: Number(r.total ?? 0),
    teto: Number(r.teto ?? 300),
    favoritos: (r.favoritos ?? []) as Favorito[],
  };
}

/**
 * Inverte o favorito e devolve o estado RESULTANTE.
 *
 * Quem sabe o estado é o banco, e não a tela: com duas abas abertas, a tela que
 * decidisse sozinha faria o oposto do que o aluno quer — ele desfavorita numa,
 * clica na outra (que ainda mostra a estrela cheia) e o clique recria.
 *
 * `null` é falha de rede ou recusa; a tela mantém a estrela como estava e avisa
 * na LINHA, sem trocar a página inteira por uma mensagem de erro.
 */
export async function alternarFavorito(
  tributo: Tributo,
  cst: string,
  cclasstrib: string | null,
): Promise<{ favorito: boolean } | { motivo: string } | null> {
  const { data, error } = await supabase.rpc("cst_alternar_favorito", {
    p_curso: cursoDaQuery(),
    p_tributo: tributo,
    p_cst: cst,
    p_cclasstrib: cclasstrib,
  });
  if (error) return null;
  const r = (data ?? {}) as { ok?: boolean; favorito?: boolean; motivo?: string };
  if (!r.ok) return { motivo: r.motivo ?? "falha" };
  return { favorito: r.favorito === true };
}

/** Grava a anotação. Texto em branco APAGA a anotação — sem um botão a mais. */
export async function anotarFavorito(id: string, nota: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("cst_anotar_favorito", {
    p_curso: cursoDaQuery(),
    p_id: id,
    p_nota: nota,
  });
  if (error) return false;
  return ((data ?? {}) as { ok?: boolean }).ok === true;
}

/**
 * O curso pelo qual o card foi aberto. Vem do `?curso=` que `aberturaDoCard` põe
 * no link; hoje o card só pertence à Pós RT, e cravar `pos-rt` sem a query seria
 * descobrir a próxima pós pelo aluno recusado.
 */
export function cursoDaQuery(): string {
  const q = new URLSearchParams(window.location.search).get("curso");
  return q && q.trim() !== "" ? q : "pos-rt";
}
